import { Reference } from '@approvalmax/types';
import {
    Button,
    Checkbox,
    CheckboxEditor,
    DateTimeEditor,
    DropdownEditor,
    FocusImplementer,
    NumberEditor,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Box, Flex } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { CellIndicator, LineItemsGroupEdit, ReadOnlyCell } from 'pages/requestForm/components';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode, RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { FC, useMemo } from 'react';
import { useCompanyBetaFeatures, useNetSuiteItemsFieldSettings } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import { SectionContainer } from '../../../../components/SectionContainer';
import { NetSuiteItem, NetSuiteSalesTaxItem } from '../../../../data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from '../../../../selectors/netsuite/netSuiteSelectors';
import { ColumnDefinition } from '../../../../selectors/types/ColumnDefinition';
import { NetSuiteItemLineExpanded } from '../../data/types';
import { calculateLineAmountsBasedOnAmount, setCurrencyPrecision } from '../../helpers';
import { useCurrencyPrecision, useGroupEditItems, useItemLines, useLineCustomFields } from '../../hooks';
import {
    checkExpensesAndItemLinesAreEmpty,
    checkLineAmortizationDateIsInvalid,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import {
    DropdownEditorContainer,
    GroupActions,
    SectionHeader,
    StyledLineItemsTable,
    StyledPriceCheckIndicator,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { netSuiteExpenseItemsState, netSuiteItemLinesState, netSuiteShowErrorsState } from '../../resources/states';
import { NetSuiteLineItemAmortizationScheduleCell } from '../NetSuiteLineItemAmortizationScheduleCell/NetSuiteLineItemAmortizationScheduleCell';
import { NetSuiteLineItemCustomerCell } from '../NetSuiteLineItemCustomerCell/NetSuiteLineItemCustomerCell';
import { NetSuiteLineItemDropdownCell } from '../NetSuiteLineItemDropdownCell/NetSuiteLineItemDropdownCell';
import { NetSuiteLineItemItemCell } from '../NetSuiteLineItemItemCell/NetSuiteLineItemItemCell';
import { NetSuiteLineItemProjectTaskCell } from '../NetSuiteLineItemProjectTaskCell/NetSuiteLineItemProjectTaskCell';
import { NetSuiteLineItemTaxCodeCell } from '../NetSuiteLineItemTaxCodeCell/NetSuiteLineItemTaxCodeCell';
import { getGroupEditItemsAvailableFields } from './NetSuiteItems.helpers';
import { messages } from './NetSuiteItems.messages';
import { ExtendedColumnDefinition, NetSuiteItemsProps } from './NetSuiteItems.types';

const { netSuiteConstants } = constants;

const NetSuiteItems: FC<NetSuiteItemsProps> = (props) => {
    const { integrationCode, workflowId, currency } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const expenseItems = useAtomValue(netSuiteExpenseItemsState);
    const itemLines = useAtomValue(netSuiteItemLinesState);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const { isNetSuiteBillsItemsProjectTasks } = useCompanyBetaFeatures(request.companyId);

    const currencyPrecision = useCurrencyPrecision(currency);

    const { departments, classes, locations, customFields, documentFields } = netSuiteEditingContext;

    const {
        onAddLine,
        onReorder,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateCustomField,
        updateLine,
        onItemChange,
    } = useItemLines(currency);

    const showErrorsOnEmptyLine = useMemo(() => {
        return checkExpensesAndItemLinesAreEmpty(expenseItems, itemLines);
    }, [expenseItems, itemLines]);

    const itemsFieldSettings = useNetSuiteItemsFieldSettings(documentFields);

    const availableFields = getGroupEditItemsAvailableFields(itemsFieldSettings, integrationCode);

    const { availableCustomFields, customFieldsColumnDefinition } = useLineCustomFields({
        customFields,
        workflowId,
        sublistType: domain.DocumentSublistType.ItemList,
        showErrors,
        showErrorsOnEmptyLine,
        ocrIsLoading,
        updateCustomField,
    });

    const {
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isShowGroupButtons,
        groupItems,
        openGroupEdit,
        handleGroupClone,
        handleGroupRemove,
        closeGroupEdit,
    } = useGroupEditItems({
        departments,
        classes,
        locations,
        workflowId,
        availableFields,
        availableCustomFields,
        items: itemLines,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateCustomField,
        onItemChange,
    });

    const tableDef = useMemo<ColumnDefinition<NetSuiteItemLineExpanded>[]>(() => {
        const definition: ExtendedColumnDefinition[] = [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  onMultiLineFieldUpdate('checked', nextState);
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox size='size-18' triState={triState} checked={checked} />
                                  </div>
                              );
                          },
                          cell: (lineItem) => {
                              return (
                                  <div
                                      onClick={() => {
                                          onFieldUpdate(lineItem.id, 'checked', !lineItem.checked);
                                      }}
                                  >
                                      <Checkbox checked={lineItem.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<NetSuiteItemLineExpanded>,
                  ]),
            {
                id: 'item',
                name: messages.itemColumnName,
                fixedWidth: 274,
                readonly: () => ocrIsLoading,
                invalid: (li) => (showErrors && !li.isEmpty && !li.item) || (showErrors && showErrorsOnEmptyLine),
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.item?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemItemCell
                                invalid={invalid}
                                value={li.item || null}
                                singleLinePerItem={false}
                                onChange={(value: NetSuiteItem | null) => {
                                    onItemChange(li.id, value);
                                }}
                                placeholder={messages.itemFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'vendorName',
                name: messages.vendorNameColumnName,
                alignRight: true,
                fixedWidth: 200,
                isHidden: itemsFieldSettings.vendorName === domain.DocumentFieldState.Hidden,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        itemsFieldSettings.vendorName === domain.DocumentFieldState.Mandatory &&
                        !li.vendorName &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                readonly: () => itemsFieldSettings.vendorName === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                cell: (li) => <ReadOnlyCell text={li.vendorName || ''} />,
            },
            {
                id: 'description',
                name: messages.descriptionColumnName,
                fixedWidth: 274,
                isHidden: itemsFieldSettings.description === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.description === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        itemsFieldSettings.description === domain.DocumentFieldState.Mandatory &&
                        !li.description &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description || ''} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.description || ''}
                                    onChange={(value) => {
                                        onFieldUpdate(li.id, 'description', value);
                                    }}
                                    placeholder={messages.descriptionFieldPlaceholder}
                                    theme='transparent'
                                    maxLength={netSuiteConstants.descriptionMaxLength}
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'qty',
                name: messages.qtyColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => ocrIsLoading,
                invalid: (li) =>
                    (showErrors && !li.isEmpty && typeof li.quantity !== 'number') ||
                    (showErrors && showErrorsOnEmptyLine),
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.quantity !== null ? intl.formatNumber(li.quantity, 'auto') : ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        invalid={invalid}
                                        value={li.quantity !== null ? li.quantity : null}
                                        onChange={(newValue: number | null) => {
                                            onFieldUpdate(li.id, 'quantity', newValue);
                                        }}
                                        placeholder={messages.qtyFieldPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        max={netSuiteConstants.quantityMaxValue}
                                        min={0}
                                        precision={5}
                                        displayFormat='currency'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'units',
                name: messages.unitsColumnName,
                fixedWidth: 160,
                isHidden: itemsFieldSettings.units === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.units === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (itemsFieldSettings.units === domain.DocumentFieldState.Mandatory && !li.units && !li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.units?.text ?? ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <DropdownEditorContainer>
                                    <PlainDataProvider items={li.unitList ?? []}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={li.units || null}
                                            singleLinePerItem={false}
                                            onChange={(value: domain.NetSuiteUnitItem | null) => {
                                                updateLine(li.id, {
                                                    units: value,
                                                    unitPrice: setCurrencyPrecision(
                                                        value?.catalogUnitPrice,
                                                        currencyPrecision
                                                    ),
                                                    defaultRate: setCurrencyPrecision(
                                                        value?.catalogUnitPrice,
                                                        currencyPrecision
                                                    ),
                                                });
                                            }}
                                            placeholder={messages.unitsFieldPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                </DropdownEditorContainer>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'unitPrice',
                name: messages.unitPriceColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => ocrIsLoading,
                invalid: (li) =>
                    (showErrors && !li.isEmpty && typeof li.unitPrice !== 'number') ||
                    (showErrors && showErrorsOnEmptyLine),
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.unitPrice !== null ? intl.formatNumber(li.unitPrice, currencyPrecision) : ''}
                        />
                    ) : (
                        <StyledPriceCheckIndicator
                            actualPrice={typeof li.unitPrice === 'number' ? li.unitPrice : undefined}
                            expectedPrice={typeof li.defaultRate === 'number' ? li.defaultRate : undefined}
                            disabled={false}
                            integrationType={domain.IntegrationType.NetSuite}
                            integrationCode={integrationCode}
                        >
                            {({ warningLevel }) => (
                                <FocusImplementer>
                                    {({ registerFocusable }) => (
                                        <>
                                            <NumberEditor
                                                ref={registerFocusable}
                                                allowFloat
                                                invalid={invalid}
                                                value={li.unitPrice !== null ? li.unitPrice : null}
                                                onChange={(newValue: number | null) => {
                                                    onFieldUpdate(li.id, 'unitPrice', newValue);
                                                }}
                                                placeholder={messages.unitPriceFieldPlaceholder}
                                                theme='transparent'
                                                align='right'
                                                max={netSuiteConstants.unitPriceMaxValue}
                                                min={-netSuiteConstants.unitPriceMaxValue}
                                                precision={currencyPrecision && 8}
                                                displayFormat='currency'
                                                changeOnBlur
                                            />

                                            <CellIndicator $warningLevel={warningLevel} />
                                        </>
                                    )}
                                </FocusImplementer>
                            )}
                        </StyledPriceCheckIndicator>
                    ),
            },
            {
                id: 'amount',
                name: messages.amountColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => itemsFieldSettings.amount === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (typeof li.amount === 'number') {
                        const isInvalidAmount =
                            li.amount > netSuiteConstants.maxLineItemAmount ||
                            li.amount < -netSuiteConstants.maxLineItemAmount;

                        return showErrors && isInvalidAmount;
                    }

                    return showErrors && showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.amount !== null ? intl.formatNumber(li.amount, currencyPrecision) : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    ref={registerFocusable}
                                    allowFloat
                                    invalid={invalid}
                                    value={li.amount}
                                    onChange={(newValue: number | null) => {
                                        onFieldUpdate(li.id, 'amount', newValue);
                                    }}
                                    placeholder={messages.amountFieldPlaceholder}
                                    theme='transparent'
                                    align='right'
                                    max={netSuiteConstants.maxTotalAmount}
                                    min={-netSuiteConstants.maxTotalAmount}
                                    precision={currencyPrecision}
                                    displayFormat='currency'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'taxCode',
                name: messages.taxCodeColumnName,
                fixedWidth: 160,
                isHidden: itemsFieldSettings.taxCode === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.taxCode === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) =>
                    showErrors &&
                    (showErrorsOnEmptyLine ||
                        (itemsFieldSettings.taxCode === domain.DocumentFieldState.Mandatory &&
                            !li.taxCode &&
                            !li.isEmpty)),
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.taxCode?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemTaxCodeCell
                                invalid={invalid}
                                value={li.taxCode || null}
                                singleLinePerItem={false}
                                onChange={(value: NetSuiteSalesTaxItem | null) => {
                                    updateLine(li.id, {
                                        taxCode: value,
                                        taxRate: value?.rate ?? null,
                                        ...calculateLineAmountsBasedOnAmount(
                                            li.amount,
                                            value?.rate ?? null,
                                            currencyPrecision
                                        ),
                                    });
                                }}
                                placeholder={messages.taxCodeFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'taxRate',
                name: messages.taxRateColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => true,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                isHidden: itemsFieldSettings.taxRate === domain.DocumentFieldState.Hidden,
                cell: (li) => <ReadOnlyCell text={typeof li.taxRate === 'number' ? `${li.taxRate}%` : ''} />,
            },
            {
                id: 'taxAmount',
                name: messages.taxAmountColumnName,
                alignRight: true,
                fixedWidth: 160,
                invalid: (li) => {
                    if (typeof li.taxAmount === 'number') {
                        let isInvalidTaxAmount = false;

                        if (
                            li.taxAmount > netSuiteConstants.taxAmountMaxValue ||
                            li.taxAmount < -netSuiteConstants.taxAmountMaxValue
                        ) {
                            isInvalidTaxAmount = true;
                        } else {
                            const taxAmountDigitsCount = li.taxAmount.toString().replace(/[^\d]/g, '').length;

                            if (taxAmountDigitsCount > netSuiteConstants.taxAmountMaxDigitsCount) {
                                isInvalidTaxAmount = true;
                            }
                        }

                        return showErrors && isInvalidTaxAmount;
                    }

                    return showErrors && showErrorsOnEmptyLine;
                },
                readonly: () => true,
                isHidden: itemsFieldSettings.taxAmount === domain.DocumentFieldState.Hidden,
                cell: (li) => (
                    <ReadOnlyCell
                        text={li.taxAmount !== null ? intl.formatNumber(li.taxAmount, currencyPrecision) : ''}
                    />
                ),
            },
            {
                id: 'grossAmount',
                name: messages.grossAmountColumnName,
                alignRight: true,
                fixedWidth: 160,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                readonly: () => ocrIsLoading,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.grossAmount !== null ? intl.formatNumber(li.grossAmount, currencyPrecision) : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    ref={registerFocusable}
                                    allowFloat
                                    invalid={invalid}
                                    value={li.grossAmount}
                                    onChange={(newValue: number | null) => {
                                        onFieldUpdate(li.id, 'grossAmount', newValue);
                                    }}
                                    placeholder={messages.grossAmountFieldPlaceholder}
                                    theme='transparent'
                                    align='right'
                                    max={netSuiteConstants.maxTotalAmount}
                                    min={-netSuiteConstants.maxTotalAmount}
                                    precision={currencyPrecision}
                                    displayFormat='currency'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'department',
                name: messages.departmentColumnName,
                fixedWidth: 274,
                isHidden: itemsFieldSettings.department === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.department === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        itemsFieldSettings.department === domain.DocumentFieldState.Mandatory &&
                        !li.department &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.department?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={departments}
                                invalid={invalid}
                                value={li.department || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'department', value);
                                }}
                                placeholder={messages.departmentFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'class',
                name: messages.classColumnName,
                fixedWidth: 180,
                isHidden: itemsFieldSettings.class === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.class === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (itemsFieldSettings.class === domain.DocumentFieldState.Mandatory && !li.class && !li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.class?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={classes}
                                invalid={invalid}
                                value={li.class || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'class', value);
                                }}
                                placeholder={messages.classFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'location',
                name: messages.locationColumnName,
                fixedWidth: 180,
                isHidden: itemsFieldSettings.location === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.location === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        itemsFieldSettings.location === domain.DocumentFieldState.Mandatory &&
                        !li.location &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.location?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={locations}
                                invalid={invalid}
                                value={li.location || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'location', value);
                                }}
                                placeholder={messages.classFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'customer',
                name: messages.customerColumnName,
                fixedWidth: 218,
                isHidden: itemsFieldSettings.customer === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.customer === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        itemsFieldSettings.customer === domain.DocumentFieldState.Mandatory &&
                        !li.customer &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.customer?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemCustomerCell
                                invalid={invalid}
                                value={li.customer || null}
                                singleLinePerItem={false}
                                onChange={(value) => {
                                    onFieldUpdate(li.id, 'customer', value);
                                }}
                                placeholder={messages.customerFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            ...(integrationCode === domain.IntegrationCode.NetSuiteBill && isNetSuiteBillsItemsProjectTasks
                ? ([
                      {
                          id: 'projectTask',
                          name: messages.projectTaskColumnName,
                          fixedWidth: 218,
                          isHidden: itemsFieldSettings.projectTask === domain.DocumentFieldState.Hidden,
                          readonly: (li) =>
                              !li.customer ||
                              itemsFieldSettings.projectTask === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  itemsFieldSettings.projectTask === domain.DocumentFieldState.Mandatory &&
                                  !li.projectTask &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) => {
                              return readonly ? (
                                  <ReadOnlyCell text={li.projectTask?.text ?? ''} />
                              ) : (
                                  <DropdownEditorContainer>
                                      <NetSuiteLineItemProjectTaskCell
                                          invalid={invalid}
                                          value={li.projectTask || null}
                                          singleLinePerItem={false}
                                          onChange={(value) => {
                                              onFieldUpdate(li.id, 'projectTask', value);
                                          }}
                                          placeholder={messages.projectTaskFieldPlaceholder}
                                          customerId={li.customer?.id}
                                      />
                                  </DropdownEditorContainer>
                              );
                          },
                      },
                  ] as ExtendedColumnDefinition[])
                : []),
            {
                id: 'billable',
                name: messages.billableColumnName,
                fixedWidth: 80,
                isHidden: itemsFieldSettings.isBillable === domain.DocumentFieldState.Hidden,
                readonly: () => itemsFieldSettings.isBillable === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={Boolean(li.isBillable)}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isBillable', value);
                            }}
                        />
                    </Flex>
                ),
            },
            ...(integrationCode === domain.IntegrationCode.NetSuitePO
                ? ([
                      {
                          id: 'expectedReceiptDate',
                          name: messages.expectedReceiptDateColumnName,
                          fixedWidth: 218,
                          isHidden: itemsFieldSettings.expectedReceiptDate === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.expectedReceiptDate === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  itemsFieldSettings.expectedReceiptDate === domain.DocumentFieldState.Mandatory &&
                                  !li.expectedReceiptDate &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid }) => (
                              <FocusImplementer>
                                  {({ registerFocusable }) => (
                                      <>
                                          <DateTimeEditor
                                              ref={registerFocusable}
                                              invalid={invalid}
                                              value={li.expectedReceiptDate || null}
                                              onChange={(value) => {
                                                  onFieldUpdate(li.id, 'expectedReceiptDate', value);
                                              }}
                                              placeholder={messages.expectedReceiptDatePlaceholder}
                                              theme='transparent'
                                          />
                                      </>
                                  )}
                              </FocusImplementer>
                          ),
                      },
                      {
                          id: 'closed',
                          name: messages.closedColumnName,
                          fixedWidth: 80,
                          isHidden: itemsFieldSettings.isClosed === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.isClosed === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                          invalid: () => showErrors && showErrorsOnEmptyLine,
                          cell: (li, { readonly }) => (
                              <Flex alignItems='center'>
                                  <CheckboxEditor
                                      disabled={readonly}
                                      value={Boolean(li.isClosed)}
                                      onChange={(value) => {
                                          onFieldUpdate(li.id, 'isClosed', value);
                                      }}
                                  />
                              </Flex>
                          ),
                      },
                  ] as ExtendedColumnDefinition[])
                : []),
            ...(integrationCode === domain.IntegrationCode.NetSuiteBill
                ? ([
                      {
                          id: 'amortizationSchedule',
                          name: messages.amortizationSchedule,
                          fixedWidth: 218,
                          isHidden: itemsFieldSettings.amortizationSchedule === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.amortizationSchedule === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  itemsFieldSettings.amortizationSchedule === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationSchedule &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationSchedule?.text ?? ''} />
                              ) : (
                                  <DropdownEditorContainer>
                                      <NetSuiteLineItemAmortizationScheduleCell
                                          invalid={invalid}
                                          value={li.amortizationSchedule || null}
                                          singleLinePerItem={false}
                                          onChange={(value) => {
                                              onFieldUpdate(li.id, 'amortizationSchedule', value);
                                          }}
                                          placeholder={messages.customerFieldPlaceholder}
                                      />
                                  </DropdownEditorContainer>
                              ),
                      },
                      {
                          id: 'amortizationStartDate',
                          name: messages.amortizationStartDate,
                          fixedWidth: 218,
                          isHidden: itemsFieldSettings.amortizationStartDate === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.amortizationStartDate === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (checkLineAmortizationDateIsInvalid(li)) {
                                  return true;
                              }

                              if (
                                  itemsFieldSettings.amortizationStartDate === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationStartDate &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DateTimeEditor
                                              ref={registerFocusable}
                                              value={li.amortizationStartDate || null}
                                              placeholder={messages.datePlaceholder}
                                              onChange={(value) => onFieldUpdate(li.id, 'amortizationStartDate', value)}
                                              theme='transparent'
                                              invalid={invalid}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      },
                      {
                          id: 'amortizationEndDate',
                          name: messages.amortizationEndDate,
                          fixedWidth: 218,
                          isHidden: itemsFieldSettings.amortizationEndDate === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.amortizationEndDate === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (checkLineAmortizationDateIsInvalid(li)) {
                                  return true;
                              }

                              if (
                                  itemsFieldSettings.amortizationEndDate === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationEndDate &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DateTimeEditor
                                              ref={registerFocusable}
                                              value={li.amortizationEndDate || null}
                                              placeholder={messages.datePlaceholder}
                                              onChange={(value) => onFieldUpdate(li.id, 'amortizationEndDate', value)}
                                              theme='transparent'
                                              invalid={invalid}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      },
                      {
                          id: 'amortizationResidual',
                          name: messages.amortizationResidual,
                          alignRight: true,
                          fixedWidth: 160,
                          isHidden: itemsFieldSettings.amortizationResidual === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              itemsFieldSettings.amortizationResidual === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  itemsFieldSettings.amortizationResidual === domain.DocumentFieldState.Mandatory &&
                                  typeof li.amortizationResidual !== 'number' &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) => {
                              return readonly ? (
                                  <ReadOnlyCell
                                      text={
                                          typeof li.amortizationResidual === 'number'
                                              ? intl.formatNumber(li.amortizationResidual, currencyPrecision)
                                              : ''
                                      }
                                  />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <>
                                              <NumberEditor
                                                  ref={registerFocusable}
                                                  allowFloat
                                                  invalid={invalid}
                                                  value={
                                                      typeof li.amortizationResidual === 'number'
                                                          ? li.amortizationResidual
                                                          : null
                                                  }
                                                  onChange={(newValue: number | null) => {
                                                      onFieldUpdate(li.id, 'amortizationResidual', newValue);
                                                  }}
                                                  placeholder={messages.inputPlaceholder}
                                                  theme='transparent'
                                                  align='right'
                                                  max={netSuiteConstants.maxTotalAmount}
                                                  min={-netSuiteConstants.maxTotalAmount}
                                                  precision={currencyPrecision}
                                                  displayFormat='currency'
                                                  changeOnBlur
                                              />
                                          </>
                                      )}
                                  </FocusImplementer>
                              );
                          },
                      },
                  ] as ExtendedColumnDefinition[])
                : []),
            ...customFieldsColumnDefinition,
        ];

        return definition.filter((col) => !col.isHidden);
    }, [
        isApproverMode,
        itemsFieldSettings,
        integrationCode,
        isNetSuiteBillsItemsProjectTasks,
        customFieldsColumnDefinition,
        onMultiLineFieldUpdate,
        onFieldUpdate,
        ocrIsLoading,
        showErrors,
        showErrorsOnEmptyLine,
        onItemChange,
        updateLine,
        currencyPrecision,
        departments,
        classes,
        locations,
    ]);

    return (
        <SectionContainer
            title={
                <SectionHeader>
                    <Box spacing='0 20'>{messages.sectionName}</Box>

                    {isShowGroupButtons && (
                        <GroupActions>
                            {isGroupEditVisible && (
                                <Button preset='small' execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </GroupActions>
                    )}
                </SectionHeader>
            }
            noPadding
        >
            <StyledLineItemsTable
                columnDefinitions={tableDef}
                lineItems={itemLines}
                invalid={false}
                onReorder={onReorder}
                onRemoveLine={onRemoveLine}
                onCloneLine={onCloneLine}
                onAddLine={onAddLine}
                canAddLine={true}
            />

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </SectionContainer>
    );
};

export default NetSuiteItems;
